import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next/pages';
import presets from 'config/package-search.json';
import { SolidButton } from 'components/Button';
import { calendarIcon } from 'assets/svgs/brandIcons';
import FieldTrigger from './FieldTrigger';

interface IDaysDropdownProps {
  minDays: number;
  maxDays: number;
  onChange: (min: number, max: number) => void;
  // WHIT-484: a hotel-only stay is counted in nights, not days.
  nights?: boolean;
}

const DaysDropdown: React.FC<IDaysDropdownProps> = ({
  minDays,
  maxDays,
  onChange,
  nights = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const label = t(
    nights ? '{{min}} - {{max}} nights' : '{{min}} - {{max}} days',
    {
      min: minDays,
      max: maxDays,
    },
  );
  const ranges = nights ? presets.nightRanges : presets.dayRanges;
  const longest = nights ? presets.maxNights : presets.maxDays;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="flex flex-col gap-1 flex-1 min-w-0 relative">
      <FieldTrigger
        label={t(nights ? 'Length of stay (nights)' : 'Duration (days)')}
        // The stay's length sits in the main row, where the value says what
        // it is; the package tour's duration is in the advanced rows, which
        // keep their labels.
        showLabel={!nights}
        icon={calendarIcon}
        open={open}
        // Always a range (WHIT-497).
        filled
        onClick={() => setOpen(o => !o)}>
        <span className="text-black">{label}</span>
      </FieldTrigger>

      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 min-w-[240px] bg-white rounded-xl shadow-lg border border-tt-gray-purple">
          <div className="p-4 space-y-3">
            {/* Manual +/- */}
            <div className="flex items-center gap-2 justify-center">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onChange(Math.max(1, minDays - 1), maxDays)}
                  className="w-7 h-7 rounded-full border border-tt-light-purple flex items-center justify-center text-tt-dark-purple hover:bg-tt-verylight-purple"
                >
                  –
                </button>
                <span className="w-8 text-center text-sm font-semibold">
                  {minDays}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    onChange(Math.min(maxDays, minDays + 1), maxDays)
                  }
                  className="w-7 h-7 rounded-full border border-tt-light-purple flex items-center justify-center text-tt-dark-purple hover:bg-tt-verylight-purple"
                >
                  +
                </button>
              </div>
              <span className="text-gray-400 mx-1">–</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    onChange(minDays, Math.max(minDays, maxDays - 1))
                  }
                  className="w-7 h-7 rounded-full border border-tt-light-purple flex items-center justify-center text-tt-dark-purple hover:bg-tt-verylight-purple"
                >
                  –
                </button>
                <span className="w-8 text-center text-sm font-semibold">
                  {maxDays}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    onChange(minDays, Math.min(longest, maxDays + 1))
                  }
                  className="w-7 h-7 rounded-full border border-tt-light-purple flex items-center justify-center text-tt-dark-purple hover:bg-tt-verylight-purple"
                >
                  +
                </button>
              </div>
            </div>
            {/* Presets */}
            <div className="grid grid-cols-3 gap-1 pt-1 border-t border-tt-gray-purple">
              {ranges.map(r => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => onChange(r.min, r.max)}
                  className={`text-xs px-1 py-1.5 rounded-lg border transition-colors ${
                    minDays === r.min && maxDays === r.max
                      ? 'bg-tt-yellow border-tt-dark-yellow font-semibold'
                      : 'border-tt-light-purple hover:bg-tt-verylight-purple'
                  }`}
                >
                  {t(r.label)}
                </button>
              ))}
            </div>
          </div>
          <div className="p-2 border-t border-tt-gray-purple">
            <SolidButton
              onClick={() => setOpen(false)}
              className="w-full !py-2.5"
            >
              {t('Confirm')}
            </SolidButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default DaysDropdown;
